import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';
import { readSwishReturn } from '../utils/swishReturn';
import { stateFor, useSwishReturn } from '../hooks/useSwishReturn';
import { SwishReturnCard } from '../components/payment/SwishReturnCard';

const DIRECT_ENDPOINTS = {
  callback: (id) => `${API}/payment/${id}/callback`,
  status: (id, nonce) => `${API}/payment/${id}/public-status/${nonce}`,
};

// Logged-in read of a tokenized request (?request_id=) or a payment remembered in
// sessionStorage when Swish returned without a nonce. Read-only: never confirms anything.
function useAuthedStatus(enabled, url, token) {
  const [view, setView] = useState({ state: 'loading', details: null });
  const tries = useRef(0);
  useEffect(() => {
    if (!enabled) return undefined;
    if (!token) { setView({ state: 'unknown', details: null }); return undefined; }
    let timer;
    const load = async () => {
      try {
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const next = stateFor(res.data?.status);
        setView({ state: next, details: res.data });
        tries.current += 1;
        if (next === 'pending' && tries.current < 10) timer = setTimeout(load, 3000);
      } catch (e) {
        const code = e.response?.status;
        setView({ state: code === 401 ? 'unknown' : code === 404 ? 'notFound' : 'pending', details: null });
      }
    };
    load();
    return () => clearTimeout(timer);
  }, [enabled, url, token]);
  return view;
}

export function PaymentReturn() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const authContext = useAuthContext();
  const token = authContext?.token || localStorage.getItem('token');

  const info = useMemo(() => readSwishReturn(params, location.search), [params, location.search]);
  const storedId = useMemo(() => sessionStorage.getItem('pending_payment_id'), []);
  useEffect(() => { sessionStorage.removeItem('pending_payment_id'); }, []);

  const hasNonce = !!(info.paymentId && info.nonce);
  const requestMode = !hasNonce && !!info.requestId;
  const sessionId = !hasNonce && !requestMode ? (info.paymentId || storedId) : null;

  const direct = useSwishReturn(info, DIRECT_ENDPOINTS);
  const request = useAuthedStatus(requestMode, `${API}/payments/request-status/${info.requestId}`, token);
  const session = useAuthedStatus(!!sessionId, `${API}/payment/${sessionId}`, token);

  let view = { state: 'unknown', details: null, reason: null, ticketReported: false };
  if (hasNonce) view = direct;
  else if (requestMode) view = request;
  else if (sessionId) view = session;

  const d = view.details || {};
  const details = {
    amount: d.amount,
    currency: d.currency || 'SEK',
    recipient: d.recipient_name || d.recipient?.name || d.recipient || d.recipient_alias,
    reference: d.trans_id,
  };

  return (
    <SwishReturnCard
      t={t}
      state={view.state}
      reason={view.reason}
      details={details}
      ticketReported={view.ticketReported}
      onHome={() => nav('/')}
      onRetry={() => nav('/')}
      onLogin={() => { sessionStorage.setItem('post_login_redirect', window.location.pathname + window.location.search); nav('/'); }}
    />
  );
}
